import { create } from 'zustand';
import type { Card, Player, Room } from '../types';

export type PageType = 'login' | 'hall' | 'game';

interface GameState {
  currentGenre: number;
  currentCards: Card[];
  lastPlayer: number | null;
  lastGenre: number | null;
  lastCards: Card[];
  isFirstRound: boolean;
}

interface GameStore {
  isLoggedIn: boolean;
  userId: string;
  userName: string;
  token: string;
  setAuth: (userId: string, userName: string, token: string) => void;
  logout: () => void;

  currentPage: PageType;
  setPage: (page: PageType) => void;

  rooms: { id: number; name: string; playerCount: number; status: string }[];
  currentRoom: Room | null;
  setRooms: (rooms: { id: number; name: string; playerCount: number; status: string }[]) => void;
  setCurrentRoom: (room: Room | null) => void;

  gameState: GameState | null;
  myPosition: number;
  setGameState: (state: GameState | null) => void;
  setMyPosition: (pos: number) => void;

  ws: WebSocket | null;
  setWs: (ws: WebSocket | null) => void;

  updateMyCards: (cards: Card[]) => void;
  updateRoom: (room: Room) => void;
}

export const useGameStore = create<GameStore>((set) => ({
  isLoggedIn: false,
  userId: '',
  userName: '',
  token: '',
  setAuth: (userId, userName, token) => set({ isLoggedIn: true, userId, userName, token }),
  logout: () => set({ isLoggedIn: false, userId: '', userName: '', token: '', currentPage: 'login', currentRoom: null }),

  currentPage: 'hall',
  setPage: (page) => set({ currentPage: page }),

  rooms: [],
  currentRoom: null,
  setRooms: (rooms) => set({ rooms }),
  setCurrentRoom: (room) => set({ currentRoom: room }),

  gameState: null,
  myPosition: 0,
  setGameState: (state) => set({ gameState: state }),
  setMyPosition: (pos) => set({ myPosition: pos }),

  ws: null,
  setWs: (ws) => set({ ws }),

  updateMyCards: (cards) => set((state) => {
    if (!state.currentRoom) return state;
    const newPlayers = state.currentRoom.players.map((p, i) => {
      if (i === state.myPosition && p) {
        return { ...p, hand: cards };
      }
      return p;
    });
    return { currentRoom: { ...state.currentRoom, players: newPlayers } };
  }),

  updateRoom: (room) => set((state) => {
    const rooms = state.rooms.map(r => r.id === room.id ? { ...r, ...room } : r);
    const currentRoom = state.currentRoom?.id === room.id ? room : state.currentRoom;
    return { rooms, currentRoom };
  }),
}));

export type { Card, Player, Room };